'use client';

import Image from 'next/image';
import { PortableText, type PortableTextComponents } from '@portabletext/react';

import { GroqImage } from '@/helpers/Image';
import type { PagePatrickAffiliateSchema } from '@/sanity/pages/PagePatrickAffiliateTemplate';
import { measuredRichText } from './richtext';
import ChevronCta from './ChevronCta';

import waveDots from '@/public/fabio-affiliate/wave-dots.png';
import edgeLines from '@/public/patrick-affiliate/edge-lines.svg';

/**
 * "Your edge deserves BIG capital." — Figma 8841:2693 (desktop) / 8811:4295
 * (mobile), plus the arcs of 8841:1756 that sit behind the truck.
 *
 * The truck cut-out overlaps the card grid (a negative bottom margin in the
 * design), so the grid is stacked above it and the truck fades out through a
 * mask before it reaches the cards.
 */

/** Card copy is Geist Light with the lead words in Medium. */
const cardRichText: PortableTextComponents = {
  block: {
    normal: ({ children }) => <p className="m-0">{children}</p>,
  },
  marks: {
    strong: ({ children }) => <strong className="font-[500]">{children}</strong>,
  },
};

function DotGrid() {
  return (
    <span aria-hidden className="grid shrink-0 grid-cols-3 gap-[0.5rem]">
      {Array.from({ length: 9 }, (_, i) => (
        <span key={i} className="bg-[#6DA991] w-[0.375rem] h-[0.375rem]" />
      ))}
    </span>
  );
}

export default function EdgeSection({
  data,
}: {
  data: PagePatrickAffiliateSchema;
}) {
  if (
    !data?.edgeChip &&
    !data?.edgeHeading?.length &&
    !data?.edgeText &&
    !data?.edgeItems?.length &&
    !data?.edgeImage &&
    !data?.edgeCTA
  )
    return null;

  return (
    <section className="flex overflow-hidden relative flex-col items-center bg-white gap-[4rem] px-[2rem] pt-[8rem] lg:px-0">
      {/* Arcs behind the truck — desktop only, and wider than the viewport. */}
      <Image
        src={edgeLines}
        alt=""
        aria-hidden
        className="hidden absolute h-auto pointer-events-none lg:block left-[-2.125rem] top-[40.125rem] w-[243.625rem] max-w-none"
      />

      {(data?.edgeChip || data?.edgeHeading?.length > 0 || data?.edgeText) && (
        <div className="flex relative flex-col items-center w-full gap-[2.25rem] lg:w-[68.375rem]">
          <div className="flex flex-col items-center w-full gap-[1.25rem]">
            {data?.edgeChip && (
              <p className="m-0 italic text-center text-black font-blacker text-[1.875rem] leading-[1.5]">
                {data.edgeChip}
              </p>
            )}
            {data?.edgeHeading?.length > 0 && (
              <div className="richtext richtext-center prose-headings:text-black prose-headings:font-geist prose-headings:font-[500] prose-headings:leading-[1.1] prose-headings:text-[4.75rem] prose-headings:tracking-[-0.2375rem] lg:prose-headings:text-[8rem] lg:prose-headings:tracking-[-0.4rem] lg:w-[66.875rem] [--hl-tracking:-0.03em]">
                <PortableText
                  value={data.edgeHeading}
                  components={measuredRichText}
                />
              </div>
            )}
          </div>
          {data?.edgeText && (
            <p className="m-0 text-center text-black/70 font-geist font-[400] text-[2.25rem] leading-[1.33] w-[43.25rem] max-w-full">
              {data.edgeText}
            </p>
          )}
        </div>
      )}

      <div className="flex relative flex-col items-center w-full lg:w-[159.5rem]">
        {data?.edgeImage && (
          <div
            className="overflow-hidden relative z-0 pointer-events-none w-[49.75rem] h-[29.375rem] -mb-[4rem] lg:w-[114.15rem] lg:h-[67.375rem] lg:-mb-[29.125rem]"
            style={{
              WebkitMaskImage:
                'linear-gradient(to bottom, #000 45%, transparent 90%)',
              maskImage: 'linear-gradient(to bottom, #000 45%, transparent 90%)',
            }}
          >
            <GroqImage
              src={data.edgeImage}
              alt=""
              quality={100}
              // Defining style.width opts out of the helper's px/8 sizing.
              style={{ width: '117.6%' }}
              className="absolute max-w-none !w-[117.6%] !h-[112.07%] left-[-8.8%] top-[-12.07%]"
            />
          </div>
        )}

        {data?.edgeItems?.length > 0 && (
          <div className="grid relative z-10 w-full grid-cols-1 gap-[2.25rem] lg:grid-cols-3">
            {data.edgeItems.map((item, i) => (
              <div
                key={i}
                className="flex relative justify-between items-start bg-white border overflow-clip border-[#2F2F2F]/10 p-[3rem] min-h-[24.75rem]"
              >
                <div
                  aria-hidden
                  className="absolute inset-0 opacity-20 pointer-events-none"
                  style={{
                    backgroundImage: `url(${waveDots.src})`,
                    backgroundSize: 'cover',
                    WebkitMaskImage:
                      'linear-gradient(to top, #000 0%, transparent 60%)',
                    maskImage: 'linear-gradient(to top, #000 0%, transparent 60%)',
                  }}
                />

                <div className="flex relative flex-col flex-1 justify-between min-w-0 h-full gap-[3rem]">
                  <div className="w-fit border-[0.0945rem] border-[#001B14]/10 p-[0.567rem] rounded-[0.378rem]">
                    <div className="flex justify-center items-center bg-[#001B14]/5 rounded-[0.32rem] w-[7.367rem] h-[7.367rem]">
                      <span className="italic font-gestura font-light text-[#196551] text-[4.5rem] leading-[1.15] tracking-[-0.225rem]">
                        {String(i + 1).padStart(2, '0')}
                      </span>
                    </div>
                  </div>
                  {item?.text?.length > 0 && (
                    <div className="font-geist font-light text-[#001B14] text-[2.75rem] leading-[1.3] tracking-[-0.0825rem]">
                      <PortableText
                        value={item.text}
                        components={cardRichText}
                      />
                    </div>
                  )}
                </div>

                <DotGrid />
              </div>
            ))}
          </div>
        )}
      </div>

      {data?.edgeCTA && (
        <div className="flex relative justify-center w-full lg:w-auto">
          <ChevronCta
            data={data.edgeCTA}
            language={data?.settings?.language}
          />
        </div>
      )}
    </section>
  );
}
